import { useQuery } from "@tanstack/react-query";
import { type BaseModalProps, BaseModal, ModalCloseCross } from "../../../../components/modals/BaseModal";
import { TypedTable } from "../../../../components/TypedTable";
import { getImputacionesFase, type Consumo, type ImputacionFase } from "../../../../ts/api/API";
import { Constantes } from "../../../../ts/constantes";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Loading, QueryError } from "../../../../components/Loading/Loading";

interface VerImputacionesFaseProps extends BaseModalProps {
	readonly idOF: number;
	readonly idFase: number;
	readonly descConjunto: string;
}
export function ModalVerImputacionesFase(props: VerImputacionesFaseProps) {
	const [focusedImputacion, setFocusedImputacion] = useState<ImputacionFase | null>(null);

	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h2 className="modal-title">
					<span>Cerrar Fase:</span>
					<span>OF #{props.idOF}</span>
					<br />
					<span>{props.descConjunto}</span>
					{Constantes.existenFases ? (
						<span>- Fase {props.idFase}</span>
					) : null}
				</h2>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ minWidth: "33vw" }}>
				<h3>Imputaciones de la Fase</h3>
				<TableImputaciones
					idOF={props.idOF}
					idFase={props.idFase}
					focusedImputacion={focusedImputacion}
					setFocusedImputacion={setFocusedImputacion}
				/>

				{focusedImputacion ? (
					<div style={{ marginTop: "20px" }}>
						<h4>Consumos de la Imputación Seleccionada</h4>
						<TableDefectuososDesperdiciados
							imputacion={focusedImputacion}
						/>
					</div>
				) : null}
			</div>
			<div className="modal-footer">
				<button type="button" className="btnSecondary" onClick={props.close}>
					Cerrar
				</button>
			</div>
		</BaseModal>
	);
}
interface TableImputacionesProps {
	readonly idOF: number;
	readonly idFase: number;
	readonly focusedImputacion: ImputacionFase | null;
	readonly setFocusedImputacion: (imputacion: ImputacionFase | null) => void;
}
function TableImputaciones(props: TableImputacionesProps) {
	const { data, error, isPending } = useImputaciones(props.idOF, props.idFase);

	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<ImputacionFase>();

		const timeFormatter = new Intl.DateTimeFormat("es-ES", { hour: "numeric", minute: "numeric" });
		const formatTime = (ts: number | null) => ts != null ? timeFormatter.format(ts * 1000) : "";

		const accesorFecha = (row: ImputacionFase) => {
			return row.inicio ?? row.final ?? row.fecha_imputacion ?? 0;
		};
		const getDayCell = (row: ImputacionFase) => {
			const value = row.inicio ?? row.final ?? row.fecha_imputacion;
			if (value == null) return "-";

			const formatter = new Intl.DateTimeFormat("es-ES");
			if (DiasDistintos(row.inicio, row.final)) {
				return <span style={{ color: "red", fontWeight: "bold" }}>
					{formatter.format(row.inicio! * 1000)}-{formatter.format(row.final! * 1000)}
				</span>;
			}
			return formatter.format(value * 1000);
		};
		const getFinCell = (row: ImputacionFase) => {
			const timeFormatted = formatTime(row.final);
			if (DiasDistintos(row.inicio, row.final)) {
				return <span style={{ color: "red", fontWeight: "bold" }}>{timeFormatted}</span>;
			}
			return timeFormatted;
		};

		return [
			columnHelper.accessor("cantidad", { header: "Cantidad Producida" }),
			columnHelper.accessor("nombreOperario", { header: "Representante" }),
			columnHelper.accessor(accesorFecha, {
				header: "Fecha", cell: info => getDayCell(info.row.original)
			}),
			columnHelper.accessor("inicio", { header: "Inicio", cell: info => formatTime(info.getValue()) }),
			columnHelper.accessor("final", {
				header: "Fin", cell: info => getFinCell(info.row.original)
			}),
		];
	}, []);

	if (error) return <QueryError error={error} fallback="Error al cargar las imputaciones" />;
	if (isPending) return <Loading />;
	if (data.imputaciones.length === 0) return <div>No hay imputaciones para esta fase.</div>;

	return (
		<TypedTable
			className="dataTable display"
			data={data.imputaciones}
			columns={columns}
			pagination

			onRowClick={(_e, data) => props.setFocusedImputacion(data)}
			rowClassName={(data) => (data === props.focusedImputacion ? "clicked" : "")}
		/>
	);
}

function TableDefectuososDesperdiciados(props: { readonly imputacion: ImputacionFase; }) {
	const imputacion = props.imputacion;

	return (
		<div className="sub-table-defectuosos mt-3">
			<table className="table table-bordered">
				<thead>
					<tr>
						<th>Artículo</th>
						<th>Lote</th>
						<th>Desperdiciados</th>
						<th>Defectuosos</th>
					</tr>
				</thead>

				<tbody>
					{imputacion.consumos.map((consumo: Consumo) => {
						return (
							<tr key={`${consumo.idArticulo}_F${consumo.faseArticulo}`}>
								<td>{consumo.descripcion}</td>
								<td>{consumo.loteArticulo}</td>
								<td>{consumo.cantidadDesperdiciada}</td>
								<td>{consumo.cantidadDefectuosa}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}


function useImputaciones(idOF: number, idFase: number) {
	return useQuery({
		queryKey: ["imputacionesFase", idOF, idFase],
		queryFn: () => getImputacionesFase(idOF, idFase)
	});
}
function DiasDistintos(ts1: number | null, ts2: number | null): boolean {
	if (ts1 == null || ts2 == null) return false;
	const date1 = new Date(ts1 * 1000);
	const date2 = new Date(ts2 * 1000);
	return date1.toDateString() !== date2.toDateString();
}